// Define action creators that dispatch actions:
import config from '../assets/config';
import { environmentApi } from '../features/api/environmentApiSlice';
import internalApi, { THROW_ON_ERROR_V1, THROW_ON_ERROR_V2 } from '../assets/internalApi';
import { getLocalErrorCode } from '../assets/lib';
import { clearLastErrorCode, setLastErrorCode } from './pageActions';
import { setShowHasBillingAddressBadge, setShowHasBillingInfoBadge } from './billingInfoActions';
import { setOriginalState } from '../features/settings/redux/settingsActions';

import { PrerenderUserAction } from '../features/PrerenderUser';

import {
  ERASE_PAGE_REDUCER,
  CACHEDPAGES_RESET,
  CACHINGACTIVITY_RESET,
  REVOKE_INVITATION_SUCCEEDED,
  REVOKE_INVITATION_FAILED,
  REVOKE_INVITATION_IN_PROGRESS,
} from './types';

import { keycloakLogout } from '../features/auth';

const API_URL = config.apiUrl.authServer;

const SKIP_CLEAR_ERROR = true;
const DONT_SKIP_CLEAR_ERROR = false;

const doGetPrerenderUser = async (dispatch, skipErrorClear = DONT_SKIP_CLEAR_ERROR, { keepSessionOnError } = {}) => {
  try {
    dispatch({ type: PrerenderUserAction.UserLoadingSet, payload: { value: true } });
    const requestUri = `${config.apiUrl.dataServer}/api/v2/user`;
    const data = await internalApi('get', requestUri, null);
    dispatch({ type: PrerenderUserAction.UserSet, payload: data });
    if (!skipErrorClear) {
      dispatch(clearLastErrorCode());
    }
    dispatch(setShowHasBillingAddressBadge(!data.hasBillingAddress));
    dispatch(setShowHasBillingInfoBadge(!data.hasBillingInfo));
    dispatch(setOriginalState(data));
    return data;
  } catch (error) {
    if (!keepSessionOnError) {
      dispatch({ type: PrerenderUserAction.UserAnauthorized });
    }
    dispatch({ type: PrerenderUserAction.UserLoadingSet, payload: { value: false } });
    throw error;
  }
};

export const getPrerenderUserThrows = () => async (dispatch) => {
  await doGetPrerenderUser(dispatch);
};

// Re-reads the user and resolves with the fresh record, so a caller can act on a
// server-derived field (e.g. `onboardingSurveyCompleted`, which dashboard-api
// recomputes on every call). It keeps the session on failure: clearing it would
// unmount the page that asked for the read, together with whatever retry that page
// offers. A real 401 still reaches the session modal through the internalApi
// response interceptor, so nothing is swallowed (PRE-3350).
export const refreshPrerenderUser = () => async (dispatch) =>
  doGetPrerenderUser(dispatch, SKIP_CLEAR_ERROR, { keepSessionOnError: true });

export const getPrerenderUser = () => async (dispatch) => {
  try {
    await doGetPrerenderUser(dispatch);
  } catch (error) {
    dispatch(setLastErrorCode(getLocalErrorCode(error)));
  }
};

export const setCacheFreshness = (prerenderUser, value) => async (dispatch) => {
  try {
    dispatch({ type: PrerenderUserAction.UserLoadingSet, payload: { value: true } });
    const requestUri = `${config.apiUrl.dataServer}/api/set-cache-freshness`;
    await internalApi('post', requestUri, { value });
    dispatch({ type: PrerenderUserAction.UserSet, payload: { ...prerenderUser, cacheFreshness: parseFloat(value) } });
    dispatch(environmentApi.util.invalidateTags(['EnvironmentHistory']));
    dispatch(clearLastErrorCode());
  } catch (error) {
    dispatch({ type: PrerenderUserAction.UserLoadingSet, payload: { value: false } });
    dispatch(setLastErrorCode(getLocalErrorCode(error)));
  }
};

export const togglePauseRecaching = (prerenderUser, onSuccess) => async (dispatch) => {
  try {
    dispatch({ type: PrerenderUserAction.UserLoadingSet, payload: { value: true } });
    const requestUri = `${config.apiUrl.dataServer}/api/toggle-pause-recaching`;
    await internalApi('post', requestUri, {});
    dispatch({
      type: PrerenderUserAction.UserSet,
      payload: { ...prerenderUser, userPausedRecaching: !prerenderUser.userPausedRecaching },
    });
    dispatch(clearLastErrorCode());
    if (onSuccess) onSuccess();
  } catch (error) {
    dispatch({ type: PrerenderUserAction.UserLoadingSet, payload: { value: false } });
    dispatch(setLastErrorCode(getLocalErrorCode(error)));
  }
};

// ---------------------------------------

export const setShowOnboarding = (value) => ({ type: PrerenderUserAction.UserShowOnboardingSet, payload: value });

export const signOutUser = () => async (dispatch) => {
  keycloakLogout();
  dispatch({ type: CACHINGACTIVITY_RESET });
  dispatch({ type: ERASE_PAGE_REDUCER });
  dispatch({ type: CACHEDPAGES_RESET });
  dispatch({ type: PrerenderUserAction.UserAnauthorized });
  dispatch({ type: PrerenderUserAction.UserReset });
};

export const regenerateApiToken = async (dispatch) => {
  try {
    const { newToken } = await internalApi('post', `${API_URL}/api/regenerate-token`, {}, THROW_ON_ERROR_V1);
    dispatch(clearLastErrorCode());
    dispatch({ type: PrerenderUserAction.UserTokenSet, payload: newToken });
  } catch (error) {
    dispatch(setLastErrorCode(getLocalErrorCode(error)));
    throw error; // Notify the caller that the call failed
  }
};

export const updateTrackingCodeInstalledLocal = (installed) => ({
  type: PrerenderUserAction.UserTrackingCodeSet,
  payload: { installed },
});

export const revokeInvitation = (emailInput) => async (dispatch) => {
  const email = emailInput.trim();
  try {
    dispatch({ type: REVOKE_INVITATION_IN_PROGRESS });
    const { invitationLink } = await internalApi(
      'post',
      `${API_URL}/api/v2/revoke-invitation`,
      { email },
      THROW_ON_ERROR_V2
    );
    dispatch(clearLastErrorCode());
    dispatch({ type: REVOKE_INVITATION_SUCCEEDED, payload: { email, invitationLink } });
  } catch (error) {
    dispatch(setLastErrorCode(getLocalErrorCode(error)));
    dispatch({ type: REVOKE_INVITATION_FAILED, payload: { email, ...error } });
  }
};

export const decreaseNotificationCount = (readAmout) => async (dispatch) => {
  dispatch({ type: PrerenderUserAction.UserUnreadNotificationsDecrease, payload: readAmout });
};

export const increaseNotificationCount = (amount) => async (dispatch) => {
  dispatch({ type: PrerenderUserAction.UserUnreadNotificationsIncrease, payload: amount });
};

export const prerenderUserPatch = (updatedProperties) => async (dispatch) => {
  dispatch({ type: PrerenderUserAction.UserPatch, payload: updatedProperties });
};
